import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Menu as MenuIcon, ShoppingCart, LogOut, Loader2, Users, FileText, Wrench, Receipt } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from './NotificationBell';
import useIsPhone from '../../hooks/useIsPhone';
import api from '../../api/axios';

// Délai avant d'interroger le serveur. Assez court pour rester réactif,
// assez long pour ne pas lancer une requête par frappe.
const DELAI_SAISIE_MS = 250;
const MIN_CARACTERES = 2;

// Ordre d'affichage des catégories + comment naviguer vers un résultat.
// Les résultats sont déjà filtrés côté serveur selon les onglets accordés :
// une catégorie absente ici ne veut pas dire qu'elle est vide, mais qu'elle
// n'est pas accessible à cet utilisateur.
const CATEGORIES = [
  {
    cle: 'contacts', label: 'Contacts', icon: Users,
    titre: r => `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Sans nom',
    detail: r => r.organisation || r.company || r.email || r.phone || '',
    lien: r => `/contacts/${r.id}`,
  },
  {
    cle: 'devis', label: 'Devis', icon: FileText,
    titre: r => r.numero,
    detail: r => [r.objet, [r.contact_first_name, r.contact_last_name].filter(Boolean).join(' ')].filter(Boolean).join(' · '),
    lien: r => (r.contact_id ? `/contacts/${r.contact_id}` : '/rechercher-devis'),
  },
  {
    cle: 'factures', label: 'Factures', icon: Receipt,
    titre: r => r.numero,
    detail: r => [r.objet, [r.contact_first_name, r.contact_last_name].filter(Boolean).join(' ')].filter(Boolean).join(' · '),
    lien: r => (r.contact_id ? `/contacts/${r.contact_id}` : '/rechercher-devis'),
  },
  {
    cle: 'sav', label: 'SAV', icon: Wrench,
    titre: r => r.numero_ref || r.materiel,
    detail: r => [r.materiel, r.marque_modele, [r.contact_first_name, r.contact_last_name].filter(Boolean).join(' ')].filter(Boolean).join(' · '),
    lien: r => `/sav?open=${r.id}`,
  },
];

// URL de la caisse : sous-domaine app. en production, paramètre ?pos=1 en local.
function urlCaisse() {
  if (typeof window === 'undefined') return '#';
  const host = window.location.hostname;
  const proto = window.location.protocol;
  const isLocal = host === 'localhost' || host.startsWith('127.');
  if (isLocal) {
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${proto}//${host}${port}/?pos=1`;
  }
  // Strip www. avant d'ajouter app. (sinon app.www.datashipset.com)
  return `${proto}//app.${host.replace(/^www\./, '')}/login`;
}

export default function TopBar({ onOpenMenu }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [ouvert, setOuvert] = useState(false);
  const boiteRef = useRef(null);

  // Recherche débouncée. La requête en vol est abandonnée quand la saisie
  // change, sinon une réponse lente écraserait une réponse plus récente.
  useEffect(() => {
    const terme = q.trim();
    if (terme.length < MIN_CARACTERES) { setRes(null); setChargement(false); return; }
    const ctrl = new AbortController();
    setChargement(true);
    const t = setTimeout(() => {
      api.get('/search', { params: { q: terme }, signal: ctrl.signal })
        .then(r => { setRes(r.data); setChargement(false); })
        .catch(err => { if (err.name !== 'CanceledError') setChargement(false); });
    }, DELAI_SAISIE_MS);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q]);

  // Ferme la liste au clic ailleurs.
  useEffect(() => {
    const onClic = e => { if (boiteRef.current && !boiteRef.current.contains(e.target)) setOuvert(false); };
    document.addEventListener('mousedown', onClic);
    return () => document.removeEventListener('mousedown', onClic);
  }, []);

  const aller = (lien) => {
    setOuvert(false);
    setQ('');
    setRes(null);
    navigate(lien);
  };

  const total = res ? CATEGORIES.reduce((n, c) => n + (res[c.cle]?.length || 0), 0) : 0;

  return (
    <header className="h-16 shrink-0 bg-surface/80 backdrop-blur-md border-b border-outline-variant flex justify-between items-center gap-3 px-4 sm:px-6 relative z-30">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <button onClick={onOpenMenu} className="lg:hidden text-on-surface-variant hover:text-primary shrink-0" aria-label="Menu">
          <MenuIcon size={22} />
        </button>

        <div ref={boiteRef} className="relative w-full max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none" />
          <input
            value={q}
            onChange={e => { setQ(e.target.value); setOuvert(true); }}
            onFocus={() => setOuvert(true)}
            placeholder="Rechercher un client, un devis, un SAV…"
            className="w-full bg-surface-container-low border border-outline-variant rounded-full py-2 pl-10 pr-9 text-on-surface placeholder:text-on-surface-variant focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm"
          />
          {chargement && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-outline animate-spin" />}

          {ouvert && q.trim().length >= MIN_CARACTERES && (
            <div className="absolute top-full mt-2 left-0 right-0 bg-surface-container border border-outline-variant rounded-xl shadow-2xl shadow-black/50 max-h-[70vh] overflow-y-auto py-2">
              {!res || total === 0 ? (
                <p className="px-4 py-3 text-xs text-outline">
                  {chargement ? 'Recherche…' : 'Aucun résultat'}
                </p>
              ) : (
                CATEGORIES.map(cat => {
                  const lignes = res[cat.cle] || [];
                  if (lignes.length === 0) return null;
                  return (
                    <div key={cat.cle}>
                      <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-outline flex items-center gap-1.5">
                        <cat.icon size={11} /> {cat.label}
                      </p>
                      {lignes.map(r => (
                        <button
                          key={`${cat.cle}-${r.id}`}
                          onClick={() => aller(cat.lien(r))}
                          className="w-full text-left px-4 py-2 hover:bg-surface-container-high transition-colors"
                        >
                          <p className="text-sm text-on-surface truncate">{cat.titre(r)}</p>
                          {cat.detail(r) && <p className="text-[11px] text-on-surface-variant truncate">{cat.detail(r)}</p>}
                        </button>
                      ))}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <a
          href={urlCaisse()} target="_blank" rel="noopener noreferrer"
          title="Ouvrir la caisse"
          className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary-container text-on-secondary-container hover:bg-secondary transition-colors text-sm font-medium"
        >
          <ShoppingCart size={16} />
          Caisse
        </a>

        {/* Une seule cloche a la fois : sur telephone, c'est celle de la
            barre basse (zone pouce) — la meme regle que la barre elle-meme. */}
        {!isPhone && (
          <div className="hidden lg:block">
            <NotificationBell variant="topbar" />
          </div>
        )}

        {/* Pastille d'initiales plutôt qu'une photo : l'app n'a pas d'avatars. */}
        <div
          className="h-8 w-8 rounded-full bg-surface-container-high border border-outline-variant flex items-center justify-center text-xs font-semibold text-on-surface-variant shrink-0"
          title={`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Utilisateur'}
        >
          {`${(user?.first_name || '').charAt(0)}${(user?.last_name || '').charAt(0)}`.toUpperCase() || '?'}
        </div>

        <button
          onClick={() => { logout(); navigate('/login'); }}
          title="Déconnexion"
          className="text-error hover:text-error/80 transition-colors pl-2 sm:pl-3 border-l border-outline-variant"
        >
          <LogOut size={18} />
        </button>
      </div>
    </header>
  );
}
